import { api } from '@/utils/apiClient';
import { logger } from '@/utils/logger';

/**
 * Authentication Service
 *
 * Cookie-first auth flow with optional bearer fallback support.
 */

export const login = async (email, password) => {
  try {
    return await api.post('/auth/login', { identifier: email, password });
  } catch (error) {
    logger.error('[AUTH_SERVICE] Login error:', error);
    throw error;
  }
};

export const register = async (userData) => {
  try {
    return await api.post('/auth/register', userData);
  } catch (error) {
    logger.error('[AUTH_SERVICE] Registration error:', error);
    throw error;
  }
};

export const logout = async (accessToken, refreshToken) => {
  try {
    await api.authPost('/auth/logout', refreshToken ? { refreshToken } : {}, accessToken || null);
  } catch (error) {
    logger.warn('[AUTH_SERVICE] Logout error (continuing anyway):', error);
  }
};

export const getCurrentUser = async (accessToken) => {
  try {
    const data = await api.authGet('/users/me', accessToken || null);
    return data?.user || data || null;
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const isExpectedAuthMiss =
      error?.status === 401 ||
      message.includes('authorization token missing') ||
      message.includes('invalid or expired token') ||
      message.includes('missing refresh token');

    if (!isExpectedAuthMiss) {
      logger.error('[AUTH_SERVICE] Get user error:', error);
    }
    return null;
  }
};

export const refreshAccessToken = async (refreshToken) => {
  try {
    return await api.post('/auth/refresh', refreshToken ? { refreshToken } : {});
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const isMissingRefreshToken =
      error?.status === 401 ||
      message.includes('missing refresh token') ||
      message.includes('refresh token');

    // Guests will naturally hit this path on bootstrap; treat as no-session.
    if (isMissingRefreshToken) {
      return null;
    }

    logger.error('[AUTH_SERVICE] Refresh token error:', error);
    throw error;
  }
};

export const googleLogin = async (idToken) => {
  try {
    return await api.post('/auth/google', { idToken });
  } catch (error) {
    logger.error('[AUTH_SERVICE] Google login error:', error);
    throw error;
  }
};

export const forgotPassword = async (identifier) => {
  try {
    return await api.post('/auth/forgot-password', { identifier });
  } catch (error) {
    logger.error('[AUTH_SERVICE] Forgot password error:', error);
    throw error;
  }
};

export const resetPassword = async ({ identifier, otp, newPassword }) => {
  try {
    return await api.post('/auth/reset-password', { identifier, otp, newPassword });
  } catch (error) {
    logger.error('[AUTH_SERVICE] Reset password error:', error);
    throw error;
  }
};

export const verifyResetPasswordOtp = async ({ identifier, otp }) => {
  try {
    return await api.post('/auth/verify-reset-password-otp', { identifier, otp });
  } catch (error) {
    logger.error('[AUTH_SERVICE] Verify reset password OTP error:', error);
    throw error;
  }
};
